/**
 * Badge text fitting — global.
 *
 * badge wrapping logic, primarily for the comparison chart modal or the Curated by Branch collection
 * but available for any badge in the theme.
 *
 * i've left claudes comments here for now
 *
 * `.badge > span` is inline-block, so its shrink-to-fit width is
 * `min(max-content, available)`. As soon as the text wraps that resolves to the full
 * available width, and the pill painted behind it (`.badge span::before`, see
 * snippets/badge.liquid) stretches well past the longest word. This measures the
 * line boxes and, only when the text actually wraps, pins the span to its widest line
 * so the pill hugs the copy. Single-line badges are left alone — they already fit, and
 * a sub-pixel measurement rounded onto them is what used to make them wrap.
 *
 * Hoisted here so every badge in the theme gets it.
 * Components that reflow their own layout can force a synchronous pass
 * over a subtree with `window.BranchBadges.fitAll(root)`, or coalesce one into the next
 * frame with `window.BranchBadges.refresh()`.
 */
(function () {
  'use strict';

  var SPANS = '.badge > span';

  var pending = false;
  var sizeObserver = null;
  var watched = null;
  var lastWritten = null; // span -> the width string we last wrote to it
  var observerPasses = 0; // consecutive passes triggered by our own ResizeObserver

  // Groups a range's client rects into visual lines and returns each line's extent.
  // One visual line can produce several rects (separate text nodes, inline children),
  // so rects are bucketed by vertical position rather than counted one apiece.
  function lineWidths(range) {
    var rects = range.getClientRects();
    var lines = [];
    for (var i = 0; i < rects.length; i++) {
      var r = rects[i];
      if (!r.width || !r.height) continue; // collapsed whitespace at a line end
      var line = null;
      for (var k = 0; k < lines.length; k++) {
        // Same line when the rect's midpoint sits inside the line's vertical span.
        var mid = r.top + r.height / 2;
        if (mid > lines[k].top && mid < lines[k].bottom) {
          line = lines[k];
          break;
        }
      }
      if (!line) {
        lines.push({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
        continue;
      }
      if (r.top < line.top) line.top = r.top;
      if (r.bottom > line.bottom) line.bottom = r.bottom;
      if (r.left < line.left) line.left = r.left;
      if (r.right > line.right) line.right = r.right;
    }
    var widths = [];
    for (var n = 0; n < lines.length; n++) widths.push(lines[n].right - lines[n].left);
    return widths;
  }

  // Returns true when this pass actually changed the span's width.
  function fit(span) {
    // Reset first so the text re-wraps naturally against the container width.
    span.style.width = '';
    if (!span.getClientRects().length) return false; // not rendered (closed dialog, hidden tab)

    var range = document.createRange();
    range.selectNodeContents(span);
    var lines = lineWidths(range);

    // On a single line the span is already shrink-to-fit at max-content, so the pill
    // hugs the copy without help. Writing a width there is at best a no-op and at worst
    // harmful: the measurement is sub-pixel, and a value a fraction short of the text
    // pushes the last word onto a second line. Only wrapped text needs pinning.
    var width = '';
    if (lines.length > 1) {
      var widest = 0;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i] > widest) widest = lines[i];
      }

      // Line boxes measure the text alone, and the theme sets `* { box-sizing: border-box }`,
      // so any padding/border on the span itself has to be added back in. Product tiles
      // pad the span rather than the badge, so this is not hypothetical.
      var styles = window.getComputedStyle(span);
      if (styles.boxSizing === 'border-box') {
        widest += (parseFloat(styles.paddingLeft) || 0) +
          (parseFloat(styles.paddingRight) || 0) +
          (parseFloat(styles.borderLeftWidth) || 0) +
          (parseFloat(styles.borderRightWidth) || 0);
      }

      if (widest) width = Math.ceil(widest) + 'px';
    }

    var changed = !lastWritten || lastWritten.get(span) !== width;
    if (lastWritten) lastWritten.set(span, width);
    if (width) span.style.width = width;
    return changed;
  }

  // Watch the badge's container rather than the badge itself — one less element whose
  // width we write to directly. The container is still often a flex item, so its width
  // can move with ours; schedule() handles the feedback.
  function watch(span) {
    if (!sizeObserver) return;
    var box = span.closest('.badge-container') ||
      (span.parentElement && span.parentElement.parentElement);
    if (!box || watched.has(box)) return;
    watched.add(box);
    sizeObserver.observe(box);
  }

  function fitAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var spans = scope.querySelectorAll(SPANS);
    var changed = false;
    for (var i = 0; i < spans.length; i++) {
      if (fit(spans[i])) changed = true;
      watch(spans[i]);
    }
    return changed;
  }

  // One pass per frame — resize, mutation and font-load events tend to arrive together.
  function schedule(fromObserver) {
    if (fromObserver) {
      // Badges sit in flex rows, so a width we write can resize the container we
      // observe, which notifies us straight back. That settles within a pass or two
      // (the pass that changes nothing clears the count); the cap is a hard stop so a
      // pathological layout can never spin the observer indefinitely.
      if (observerPasses >= 3) return;
      observerPasses++;
    } else {
      observerPasses = 0;
    }
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      if (!fitAll()) observerPasses = 0;
    });
  }

  function refresh() {
    schedule(false);
  }

  // Badges arrive after load all over the theme: filtered collection grids, the AJAX
  // header, quick view, section rendering. Re-run whenever any of them land.
  function watchInsertions() {
    if (typeof MutationObserver !== 'function') return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches(SPANS) || node.querySelector(SPANS)) {
            refresh();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    if (typeof WeakMap === 'function') lastWritten = new WeakMap();
    if (typeof ResizeObserver === 'function' && typeof WeakSet === 'function') {
      watched = new WeakSet();
      sizeObserver = new ResizeObserver(function () {
        schedule(true);
      });
    }
    fitAll();
    watchInsertions();
    // A container resize covers most reflows; these cover the rest — a viewport
    // breakpoint that changes font-size without changing container width, and the
    // webfont swap, which changes the text metrics we just measured.
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh);
    }
  }

  window.BranchBadges = { fit: fit, fitAll: fitAll, refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();